// The live support-need routes (bead hf-e4-events-needs-6r5.2).
//
// Subscription and mutation plumbing only. Every decision about words, layout and
// which control to show belongs to `NeedSurfaces.tsx`, which is why that file is
// testable without a server and this one is thin enough to read in one sitting.
//
// Reads and writes go through `convex/_generated/api` and nothing else, so every
// function reached is a wrapped query or mutation that resolved the caller's
// Membership and capability before its handler ran.
//
// ── THE EVENT IS CONTEXT, NEVER A SOURCE ────────────────────────────────────────
//
// `NewNeedRoute` takes an event only to pre-fill a SUGGESTION and to print the
// appointment beside the window as context. It never copies the event's times into
// the need: `suggestWindowFor` opens an hour early and closes two hours late,
// because a driver for a 10:15 appointment is needed 09:15–13:00 (NED-002), and a
// pre-fill that matched the event would teach the opposite of that on the one screen
// where somebody is deciding.

import { StatusMessage } from "@herefor/design-system";
import { ERROR_MESSAGES, NEED_SURFACE_MESSAGES as COPY } from "@herefor/product-language";
import { timeZone, type HelpMode, type TimeZoneId } from "@herefor/domain-kernel";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { eventQueries, lifecycleProblem, type GetEventResult } from "../../convex/events";
import { coverageQueries, type NeedCoverageResult } from "../../convex/coverage";
import { needMutations, needQueries } from "../../convex/needs";
import { useConnectivity } from "../../offline/offline";
import {
  EMPTY_NEED_FORM,
  relateToEvent,
  suggestWindowFor,
  toCreateInput,
  toLocalInputValue,
  toUpdateInput,
  type FieldProblem,
  type NeedDetailView,
  type NeedFormValues,
} from "./needData";
import { NeedAskPanel, PendingAskRoute } from "../requests";
import { ProxyCommitmentRoute, VolunteerRoute } from "../commitments";
import { PracticalUpdatesRoute } from "../practical-updates";
import { NeedDetail, NeedForm, type NeedAffordances, type NeedCoverageView } from "./NeedSurfaces";
import { userVisibleStateKey } from "../plan/planData";

export interface NeedRouteContext {
  readonly circleId: string;
  readonly timeZone: string;
  /** Server-decided. Never inferred here from a role (AUT-007). */
  readonly affordances: NeedAffordances;
  readonly onDone: () => void;
}

/** The event a new need hangs off, once read from the server. */
interface NeedEventContext {
  readonly eventId: string;
  readonly startsAt: number;
  readonly endsAt?: number;
  readonly windowLabel: string;
}

/* ================================================================ the create === */

export function NewNeedRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  eventId = null,
  onDone,
}: NeedRouteContext & { readonly eventId?: string | null }) {
  const zone = timeZone(zoneName);
  const create = useMutation(needMutations.create);
  // Read the event HERE rather than taking it from the plan view model: that model
  // carries formatted labels, and a window suggestion needs the instants. Skipped
  // entirely for a need that stands on its own (NED-007).
  const foundEvent = useQuery(eventQueries.get, eventId === null ? "skip" : { circleId, eventId });
  const event = eventContextFrom(eventId, foundEvent, zone);
  const [values, setValues] = useState<NeedFormValues | null>(null);
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const { actionsPaused } = useConnectivity();

  // Fail closed. Somebody who cannot ask for help is never shown a form that will
  // reject them.
  if (!affordances.canEdit) return <Unavailable />;
  // Wait for the event before suggesting a window, so the suggestion is never
  // rendered blank and then replaced under somebody's cursor.
  if (eventId !== null && foundEvent === undefined) return null;

  const current = values ?? initialForm(event, zone);

  const submit = () => {
    if (actionsPaused) return;
    const validated = toCreateInput(current, circleId, zone, event?.eventId);
    if (!validated.ok) {
      setProblems(validated.problems);
      return;
    }

    setProblems([]);
    setBusy(true);
    void create(compact(validated.input))
      .then(onDone)
      .catch(() => {
        setProblems([{ field: "form", message: ERROR_MESSAGES.serviceUnavailable.body }]);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <NeedForm
      values={current}
      onChange={setValues}
      onSubmit={submit}
      problems={problems}
      submitLabel={COPY.create}
      heading={COPY.createTitle}
      headingLevel={1}
      eventWindowLabel={event?.windowLabel ?? null}
      windowRelation={relationFor(current, event, zone, circleId)}
      busy={busy || actionsPaused}
    />
  );
}

/* ================================================================== the edit === */

export function EditNeedRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  supportNeedId,
  eventWindowLabel = null,
  onDone,
}: NeedRouteContext & {
  readonly supportNeedId: string;
  readonly eventWindowLabel?: string | null;
}) {
  const zone = timeZone(zoneName);
  const found = useQuery(needQueries.get, { circleId, supportNeedId });
  const update = useMutation(needMutations.update);
  const [values, setValues] = useState<NeedFormValues | null>(null);
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const { actionsPaused } = useConnectivity();

  if (!affordances.canEdit) return <Unavailable />;
  if (found === undefined) return null;
  if (!found.ok) return <Unavailable />;

  const original = toDetailView(found.value, zone);
  const current = values ?? formFrom(found.value, zone);

  const submit = () => {
    if (actionsPaused) return;
    const validated = toUpdateInput(current, original, circleId, zone);
    if (!validated.ok) {
      setProblems(validated.problems);
      return;
    }

    setProblems([]);
    setBusy(true);
    void update(compact(validated.input))
      .then(onDone)
      .catch(() => {
        setProblems([{ field: "form", message: ERROR_MESSAGES.serviceUnavailable.body }]);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <NeedForm
      values={current}
      onChange={setValues}
      onSubmit={submit}
      problems={problems}
      submitLabel={COPY.save}
      heading={COPY.editTitle}
      headingLevel={1}
      eventWindowLabel={eventWindowLabel}
      // §10.8: changing the window, the capacity or the help modes is material
      // (NED-009), so the organiser is told before they save rather than after.
      material={isMaterial(current, original, zone, circleId)}
      busy={busy || actionsPaused}
    />
  );
}

/* ================================================================ the detail === */

export function NeedRoute({
  circleId,
  timeZone: zoneName,
  affordances,
  supportNeedId,
  committedPeopleCount,
  canVolunteer,
  canRecordProxyCommitments,
  viewerPersonId,
  responsible = [],
  onEdit,
  onDone,
  onViewPlan,
}: NeedRouteContext & {
  readonly supportNeedId: string;
  readonly committedPeopleCount: number;
  /** Server-decided: may this Membership offer to help? (bead ...udt.2) */
  readonly canVolunteer: boolean;
  /** Server-decided from commitments.record.proxy (AUT-007). */
  readonly canRecordProxyCommitments: boolean;
  readonly viewerPersonId: string;
  /** Who is already helping, as the server derived it. */
  readonly responsible?: readonly {
    readonly personId: string;
    readonly name: string;
    readonly recordedByName: string | null;
    readonly agreementSource: string | null;
  }[];
  readonly onEdit: () => void;
  readonly onViewPlan?: () => void;
}) {
  const zone = timeZone(zoneName);
  const found = useQuery(needQueries.get, { circleId, supportNeedId });
  // THE REAL COVERAGE READ MODEL, subscribed to here rather than handed down from the
  // plan read (bead hf-9pw). Two things follow from that, and both were the point:
  //
  //   The browser stops carrying a second coverage projection. `App.tsx` used to build
  //   one from the plan read, reconstructing `confirmedCapacity` with a
  //   `need.responsible.length` fallback — a working commitment-counter sitting in a
  //   component, one shape change away from being the second answer COV-002 forbids.
  //   It is deleted; this subscription replaces it.
  //
  //   And `awaitingReply` arrives, which the plan read has no projection for. That is
  //   why this surface — the one where somebody decides whether to volunteer — was the
  //   only place that could not say who had already been asked (REQ-003).
  const coverageResult = useQuery(coverageQueries.forNeed, { circleId, supportNeedId });
  const publish = useMutation(needMutations.publish);
  const complete = useMutation(needMutations.complete);
  const cancel = useMutation(needMutations.cancel);
  const deleteDraft = useMutation(needMutations.deleteDraft);
  const [busy, setBusy] = useState(false);
  const [actionProblem, setActionProblem] = useState<string | null>(null);
  const { actionsPaused } = useConnectivity();

  if (found === undefined || coverageResult === undefined) return null;
  if (!found.ok) return <Unavailable />;
  // A coverage rejection is the same fail-closed answer a need rejection is: the
  // detail is not rendered with a number nobody stands behind.
  if (!coverageResult.ok) return <Unavailable />;

  const view = toDetailView(found.value, zone);
  const coverage = toNeedCoverageView(coverageResult.value);
  const args = { circleId, supportNeedId, expectedRevision: view.revision };
  const run = (call: () => Promise<unknown>, after: () => void) => {
    if (actionsPaused) return;
    setActionProblem(null);
    setBusy(true);
    void call()
      .then((outcome) => {
        const problem = lifecycleProblem(outcome);
        if (problem === null) after();
        else setActionProblem(problem);
      })
      .catch(() => {
        setActionProblem(ERROR_MESSAGES.serviceUnavailable.body);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <NeedDetail
        need={view}
        headingLevel={1}
        coverage={coverage}
        affordances={affordances}
        committedPeopleCount={committedPeopleCount}
        // Who could help, who has been asked, and the act of asking (beads
        // hf-e5-availability-ccp.2 and hf-e6-commitments-coverage-udt.1). Composed
        // HERE rather than inside `NeedDetail`, which is a pure component tree: these
        // are live subscriptions, and this route is where subscriptions belong.
        //
        // ONE component, not two beside each other, because "who has an open ask" is
        // the input to both halves — `NeedAskPanel` reads it once so the could-help
        // list and the audience panel cannot disagree about whether Mary is waiting.
        //
        // `canSendRequests` is the server's own flag now. ccp.2 borrowed
        // `affordances.canEdit` while the Direct Request mutation was still being
        // built and recorded the debt on its close; this is that debt paid.
        supplementary={
          <>
            {/* If THIS person has been asked about THIS need, the question comes
                first. Somebody who followed a link from their notification lands
                here, and having to hunt for a list before they can say yes is how
                an ask lapses for a reason that has nothing to do with their week.
                `hideWhenEmpty` keeps it invisible for everybody else. */}
            <PendingAskRoute
              actionsPaused={actionsPaused}
              circleId={circleId}
              hideWhenEmpty
              supportNeedId={supportNeedId}
              timeZone={zoneName}
            />
            <NeedAskPanel
              actionsPaused={actionsPaused}
              canSendRequests={affordances.canSendRequests}
              // A SECOND capability, not an inference from the first (bead hf-mtk):
              // the ask-anyone list reads the `people.view`-gated directory, so a
              // caller without it is offered no list rather than a thrown query.
              canViewPeople={affordances.canViewPeople}
              canViewAvailability={affordances.canViewAvailability}
              circleId={circleId}
              supportNeedId={supportNeedId}
              timeZone={zoneName}
              viewerPersonId={viewerPersonId}
            />
          </>
        }
        onEdit={onEdit}
        onPublish={() => {
          run(
            () => publish(args),
            () => undefined,
          );
        }}
        // NED-013: completing before the window has started is an organiser's
        // correction, recorded as one rather than passing for the ordinary case.
        onMarkDone={() => {
          run(
            () => complete({ ...args, organiserCorrection: !view.isPast }),
            () => undefined,
          );
        }}
        onCancel={() => {
          run(
            () => cancel(args),
            () => undefined,
          );
        }}
        onDeleteDraft={() => {
          run(() => deleteDraft(args), onDone);
        }}
        busy={busy || actionsPaused}
      />

      {actionProblem === null ? null : (
        <StatusMessage tone="error" live="assertive">
          {actionProblem}
        </StatusMessage>
      )}

      <PracticalUpdatesRoute
        circleId={circleId}
        supportNeedId={supportNeedId}
        timeZone={zoneName}
      />

      {/* Offering to help, and stepping back (bead hf-e6-commitments-coverage-udt.2).
          Only on a need that is actually open: a draft nobody has seen and a finished
          or cancelled one are not things to volunteer for, and offering anyway would
          send a tap the guard would only refuse. */}
      {view.state === "open" ? (
        <>
          <VolunteerRoute
            circleId={circleId}
            supportNeedId={supportNeedId}
            // The revision THIS person is looking at, sent with the claim so the server
            // can tell consent from a stale tap (invariant 4). Never re-derived.
            needRevision={view.revision}
            responsibility={view.label}
            canVolunteer={canVolunteer}
            others={responsible}
            stillNeedsSomeone={coverage.status === "needsSomeone"}
            {...(onViewPlan === undefined ? {} : { onViewPlan })}
          />
          <ProxyCommitmentRoute
            circleId={circleId}
            supportNeedId={supportNeedId}
            needRevision={view.revision}
            canRecord={canRecordProxyCommitments}
            stillNeedsSomeone={coverage.status === "needsSomeone"}
            viewerPersonId={viewerPersonId}
          />
        </>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ plumbing --- */

/**
 * The shape Convex's generated args want, from the shape zod's output gives.
 *
 * `exactOptionalPropertyTypes` is on, so `{ practicalNote?: string | undefined }` —
 * which is what a zod `.optional()` produces — is not assignable to
 * `{ practicalNote?: string }`. A mapped type preserves the optional modifier while
 * dropping `undefined` from the value, which is precisely the difference.
 */
type Exact<T> = { [K in keyof T]: Exclude<T[K], undefined> };

/**
 * Drop the keys whose value is `undefined`, so the runtime matches the type above.
 *
 * Convex omits undefined from a call anyway; doing it here means the type says so.
 */
function compact<T extends object>(value: T): Exact<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  ) as Exact<T>;
}

function Unavailable() {
  return (
    <section>
      <h1>{ERROR_MESSAGES.itemUnavailable.title}</h1>
      <StatusMessage tone="info">{ERROR_MESSAGES.itemUnavailable.body}</StatusMessage>
    </section>
  );
}

interface ServerNeed {
  readonly supportNeedId: string;
  readonly eventId?: string;
  readonly label: string;
  readonly needType: NeedFormValues["needType"];
  readonly startsAt: number;
  readonly endsAt: number;
  readonly requiredCapacity: number;
  readonly requiredHelpModes: readonly HelpMode[];
  readonly importance: NeedFormValues["importance"];
  readonly meetingDetail?: string;
  readonly practicalNote?: string;
  readonly state: "draft" | "open" | "completed" | "cancelled";
  readonly revision: number;
}

/** Turn the server's event, if there is one, into the context the form needs. */
function eventContextFrom(
  eventId: string | null,
  found: GetEventResult | undefined,
  zone: TimeZoneId,
): NeedEventContext | null {
  if (eventId === null || found?.ok !== true) return null;

  const event = found.value;
  // An event whose time is not confirmed yet is a normal record (plan §9.5), and it
  // simply cannot seed a window suggestion. The form opens empty rather than guessing.
  if (event.startsAt === undefined) return null;

  const start = toLocalInputValue(event.startsAt as never, zone).replace("T", " ");
  return {
    eventId,
    startsAt: event.startsAt,
    ...(event.endsAt === undefined ? {} : { endsAt: event.endsAt }),
    windowLabel:
      event.endsAt === undefined
        ? start
        : `${start} – ${toLocalInputValue(event.endsAt as never, zone).slice(11)}`,
  };
}

function initialForm(event: NeedEventContext | null, zone: TimeZoneId): NeedFormValues {
  if (event === null) return EMPTY_NEED_FORM;
  const suggested = suggestWindowFor(event, zone);
  return { ...EMPTY_NEED_FORM, ...suggested };
}

function relationFor(
  values: NeedFormValues,
  event: NeedEventContext | null,
  zone: TimeZoneId,
  circleId: string,
) {
  if (event === null) return "unrelated" as const;
  const parsed = toCreateInput(values, circleId, zone);
  if (!parsed.ok) return "unrelated" as const;
  return relateToEvent(
    { startsAt: parsed.input.startsAt, endsAt: parsed.input.endsAt },
    { startsAt: event.startsAt, endsAt: event.endsAt ?? event.startsAt },
  );
}

/**
 * NED-009 fixes the material set exactly: the window, the capacity and the help
 * modes. The label and the practical note are cosmetic and must not disturb anybody.
 */
function isMaterial(
  values: NeedFormValues,
  original: NeedDetailView,
  zone: TimeZoneId,
  circleId: string,
): boolean {
  const parsed = toCreateInput(values, circleId, zone);
  if (!parsed.ok) return false;

  const modesDiffer =
    parsed.input.requiredHelpModes.length !== original.requiredHelpModes.length ||
    parsed.input.requiredHelpModes.some((mode) => !original.requiredHelpModes.includes(mode));

  return (
    parsed.input.startsAt !== original.startsAt ||
    parsed.input.endsAt !== original.endsAt ||
    parsed.input.requiredCapacity !== original.requiredCapacity ||
    modesDiffer
  );
}

function toDetailView(need: ServerNeed, zone: TimeZoneId): NeedDetailView {
  const start = toLocalInputValue(need.startsAt as never, zone).replace("T", " ");
  const end = toLocalInputValue(need.endsAt as never, zone).slice(11);

  return {
    supportNeedId: need.supportNeedId,
    eventId: need.eventId ?? null,
    label: need.label,
    needType: need.needType,
    startsAt: need.startsAt,
    endsAt: need.endsAt,
    windowLabel: `${start} – ${end}`,
    requiredCapacity: need.requiredCapacity,
    requiredHelpModes: need.requiredHelpModes,
    importance: need.importance,
    meetingDetail: need.meetingDetail ?? null,
    practicalNote: need.practicalNote ?? null,
    state: need.state,
    revision: need.revision,
    // Past is a display treatment decided against the clock at render time; the
    // record never stores it (plan §10.2).
    isPast: need.endsAt < Date.now(),
  };
}

function formFrom(need: ServerNeed, zone: TimeZoneId): NeedFormValues {
  return {
    label: need.label,
    needType: need.needType,
    startsAtLocal: toLocalInputValue(need.startsAt as never, zone),
    endsAtLocal: toLocalInputValue(need.endsAt as never, zone),
    requiredCapacity: String(need.requiredCapacity),
    requiredHelpModes: need.requiredHelpModes,
    importance: need.importance,
    meetingDetail: need.meetingDetail ?? "",
    practicalNote: need.practicalNote ?? "",
  };
}

/**
 * The server's coverage answer, projected — never recomputed (COV-002, invariant 1).
 *
 * Every field passes straight through. `status` goes via `userVisibleStateKey`, which
 * is the ONE map from a kernel status to a product state and is shared with every other
 * coverage surface, and `awaitingReplyNames` is lifted out of the wire shape so the
 * pure component takes strings rather than person records it has no other use for.
 *
 * There is deliberately no arithmetic here and no fallback. The previous projection had
 * one — `need.counts?.confirmed ?? need.responsible.length` — which was unreachable and
 * therefore untested, and which counted commitments in a browser.
 */
function toNeedCoverageView(
  coverage: Extract<NeedCoverageResult, { ok: true }>["value"],
): NeedCoverageView {
  return {
    status: userVisibleStateKey(coverage.status),
    confirmedCapacity: coverage.confirmedCapacity,
    requiredCapacity: coverage.requiredCapacity,
    awaitingReplyNames: coverage.awaitingReply.map((person) => person.displayName),
  };
}
